import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, useAlert } from "@digital-services/ui";

import ErrorComponent from "@components/ErrorComponent";
import LoadingComponent from "@components/LoadingComponent";

import { ApplicationJsonModel } from "@applications/models/ApplicationJsonModel";
import { SelectedApplication } from "@users/applicationuser/models/SelectedApplication";
import { AssociatedApplicationRoles } from "@users/organizationuser/models/AssociatedApplicationRoles";

import {
  ApplicationSimplified,
  UserApplication,
  UserApplicationWithErrors
} from "../models/UserApplication";
import {
  useInviteApplicationUser,
  useRemoveUserApplication,
  useUserApplications
} from "../services/ApplicationUserService";
import { EditApplicationAssociations } from "./EditApplicationAssociations";

interface Props {
  userId: string;
  userDisplayName: string;
  applicationId?: string;
  onCancel: () => void;
  onSuccess: () => void;
}

function areRolesEqual(orgRoles: string[], newRoles: string[]): boolean {
  if (orgRoles.length !== newRoles.length) return false;

  for (const role of orgRoles) {
    const index = newRoles.findIndex(item => item === role);
    if (index === -1) return false;
  }

  return true;
}

// This components is used for
// - Edit Organization User
// - Re-Invite Application User
export function EditApplicationAssociationsForm(props: Readonly<Props>) {
  const { userId, userDisplayName, applicationId, onCancel, onSuccess } = props;

  // React Note.
  // This component rendered 6 times, because parent component renders 2 times, so 2 * 3 = 6
  // TBD, research, maybe improve

  const { t } = useTranslation(undefined, {
    keyPrefix: "application.users"
  });
  const { t: tApplications } = useTranslation(undefined, {
    keyPrefix: "user.applications"
  });
  const { t: tCommon } = useTranslation();

  const { notifyError, notifySuccess } = useAlert();

  const [selectedApplications, setSelectedApplications] = useState<
    SelectedApplication[]
  >([]);

  const [canSave, setCanSave] = useState<boolean>(false);

  const { invite: inviteApplicationUser } = useInviteApplicationUser();
  const { deleteApp: deleteApplicationUser } = useRemoveUserApplication();

  const { userApplications, isLoading, error } = useUserApplications(userId);

  const [isBusy, setIsBusy] = useState<boolean>(false);

  // Clear user selection
  useEffect(() => {
    return () => {
      setSelectedApplications([]);
    };
  }, []);

  useEffect(() => {
    if (!userApplications) return;

    const items: SelectedApplication[] = [];

    for (const item of userApplications) {
      if (item?.error) {
        continue;
      }

      const appData: UserApplication = item?.data;

      items.push({
        applicationAssociation: {
          applicationId: appData.application.id,
          applicationName: appData.application.name,
          userRoles: appData.userRoles
        },
        isValid: true
      });
    }

    setSelectedApplications(items);
  }, [userApplications]);

  useEffect(() => {
    setCanSave(selectedApplications.every(item => item.isValid));
  }, [selectedApplications]);

  function handleAppRemove(application: ApplicationJsonModel) {
    setSelectedApplications(current => {
      const applications = [...current];

      const index = applications.findIndex(
        item => item.applicationAssociation.applicationId === application.id
      );
      if (index > -1) applications.splice(index, 1);

      return applications;
    });
  }

  function handleAppRoleChange(
    applicationId: string,
    applicationName: string,
    applicationRoles: string[],
    isValid: boolean
  ) {
    const selectedAppRole = new AssociatedApplicationRoles(
      applicationId,
      applicationName,
      applicationRoles
    );

    setSelectedApplications(current => {
      const applications = [...current];

      const index = applications.findIndex(
        item => item.applicationAssociation.applicationId === applicationId
      );
      if (index > -1) applications.splice(index, 1);

      let localIsValid = isValid;

      if (selectedAppRole?.userRoles.length === 0) {
        // because of apps like "AMP" (no roles integration)
        localIsValid = true;
      }

      applications.push({
        applicationAssociation: selectedAppRole,
        isValid: localIsValid
      });

      return applications;
    });
  }

  const saveChanges = () => {
    setIsBusy(true);
    updateApplicationAssociationsIfNeeded()
      .then(() => {
        onSuccess();
      })
      .catch(() => {
        notifyError({
          header: t("save.error.message"),
          "data-testid": "edit-application-user-save-error"
        });
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const updateApplicationAssociationsIfNeeded = async () => {
    for (const selectedItem of selectedApplications) {
      const orgApp = userApplications?.find(
        (item: UserApplicationWithErrors) => {
          const app = item?.data?.application;

          if (!app) {
            return;
          }

          return app.id === selectedItem.applicationAssociation.applicationId;
        }
      );

      if (!orgApp) {
        await addApplicationAssociation(selectedItem.applicationAssociation);
      } else if (
        !areRolesEqual(
          orgApp?.data?.userRoles,
          selectedItem.applicationAssociation.userRoles
        )
      ) {
        await updateApplicationAssociation(selectedItem.applicationAssociation);
      }
    }

    // Remove Application Associations that are no longer in the selected applications
    for (const applicationAssociation of userApplications!) {
      if (applicationAssociation?.error) {
        continue;
      }

      const app = applicationAssociation?.data?.application;

      const index = selectedApplications.findIndex(
        selectedItem =>
          selectedItem.applicationAssociation.applicationId === app?.id
      );
      if (index > -1) continue;

      await removeApplicationAssociation(app, userId);
    }
  };

  const addApplicationAssociation = async (
    applicationAssociation: AssociatedApplicationRoles
  ) => {
    const { isSuccess } = await inviteApplicationUser(
      applicationAssociation.applicationId,
      userId,
      applicationAssociation.userRoles
    );

    if (isSuccess) {
      notifySuccess({
        header: t("add.success.message", {
          user: userDisplayName,
          application: applicationAssociation.applicationName
        }),
        "data-testid": "associate-application-user-save-success"
      });
    } else {
      notifyError({
        header: t("add.error.message", {
          user: userDisplayName,
          application: applicationAssociation.applicationName
        }),
        "data-testid": "associate-application-user-save-error"
      });
    }
  };

  const updateApplicationAssociation = async (
    applicationAssociation: AssociatedApplicationRoles
  ) => {
    const { isSuccess } = await inviteApplicationUser(
      applicationAssociation.applicationId,
      userId,
      applicationAssociation.userRoles
    );

    if (isSuccess) {
      notifySuccess({
        header: t("update.success.message", {
          user: userDisplayName,
          application: applicationAssociation.applicationName
        }),
        "data-testid": "update-application-user-save-success"
      });
    } else {
      notifyError({
        header: t("update.error.message", {
          user: userDisplayName,
          application: applicationAssociation.applicationName
        }),
        "data-testid": "update-application-user-save-error"
      });
    }
  };

  const removeApplicationAssociation = async (
    application: ApplicationSimplified,
    userId: string
  ) => {
    const { error } = await deleteApplicationUser(application.id, userId);

    if (error) {
      notifyError({
        header: t("delete.error.message", {
          user: userDisplayName,
          application: application.name
        }),
        "data-testid": "delete-application-user-save-error"
      });
    } else {
      notifySuccess({
        header: t("delete.success.message", {
          user: userDisplayName,
          application: application.name
        }),
        "data-testid": "delete-application-user-save-success"
      });
    }
  };

  if (isLoading)
    return <LoadingComponent message={tApplications("load.message")} />;
  if (error)
    return <ErrorComponent message={tApplications("load.error.message")} />;

  return (
    <>
      <EditApplicationAssociations
        applicationId={applicationId}
        onAppRoleChange={handleAppRoleChange}
        onAppRemove={handleAppRemove}
        userId={userId}
        isDisabled={isBusy}
        userApplications={userApplications!}
      />
      <div
        className="flex flex-row-reverse gap-4"
        data-testid="edit-application-associations-form-buttons"
      >
        <Button
          variant="primary"
          data-testid="save"
          isLoading={isBusy}
          disabled={!canSave}
          onClick={saveChanges}
        >
          {tCommon("button.save.label")}
        </Button>
        <Button
          disabled={isBusy}
          data-testid="cancel"
          onClick={() => onCancel()}
        >
          {tCommon("button.cancel.label")}
        </Button>
      </div>
    </>
  );
}
